import React from 'react';

import { Aedu } from 'global';

import { isCoronavirusWork } from 'js/coronavirus/coronavirus_research_detector';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';

import {
  BulkDownloadableWork,
  bulkDownloadDownloadSource,
  bulkDownloadImpressionSource,
  BulkDownloadTrigger,
  giveGatedDownloadOnUpsellClose,
  externalFallbackUrl,
  navigateToCheckout,
  updateBulkDownloadLookback,
} from './bulk_download_helper';
import renderModal from 'js/utils/renderModal';
import { fetchFeatureEnabled } from 'js/research_radar/api/researchRadarApi';
import TransactionalPricingModal from './TransactionalPricingModal/TransactionalPricingModal';
import {
  fetchBulkDownloadCountsCached,
  fetchPreviewWorksCached,
} from './bulk_download_counts_cache';
import { loadWonderchatScript } from './wonderchat_widget';
import { abTest as TransactionalPricingV3AbTest } from 'js/ab_tests/tests/bulk_download_2026_q1_transactional_pricing_v3';
import { abTest as ModalRefactorAbTest } from 'js/ab_tests/tests/bulk_download_2026_q2_modal_refactor';
import {
  ModalContext,
  resolveArm,
} from 'js/bulk_download/autotest/AutotestBulkDownload';
import { isRVariantArm } from 'js/bulk_download/autotest/rVariants';
import { abTest as CiterightMarketingDownloadAbTest } from 'js/ab_tests/tests/citeright_2026_04_marketing_modal_download_flow';

// LISWP triggers eligible for the transactional pricing A/B test.
// Only these triggers (regular BD upsell on LISWP) should bucket users.
const TRANSACTIONAL_PRICING_TRIGGERS: ReadonlySet<string> = new Set([
  'swp-toolbar-work-card-download-button',
  'swp-bulk-download-button',
  'swp-sticky-header-work-card-download-button',
  'swp-sticky-header-bulk-download-button',
]);

type Options = {
  trigger: BulkDownloadTrigger;
  work: BulkDownloadableWork;
  // `gatedDownload` indicates whether the single-work download should be gated
  // behind the upsell (i.e., user gets the download when the modal closes).
  gatedDownload?: boolean;
  autoStartBulkDownload?: boolean;
  purchased?: boolean;
  readerMessageLimitReached?: boolean;
  setReaderMessageLimitReached?: (value: boolean) => void;
  /** Whether the user passes the synchronous CiteRight encore-email
   * eligibility filter on this work (post_download_data API). Sourced
   * server-side from Treatments::CiterightDownloadInvitation
   * .eligible_at_download_time?. Used to gate AB test bucketing for the
   * CITE-232 marketing modal. */
  citerightMarketingModalEligible?: boolean;
  /** External publisher landing page URL. When present (system-owned works whose
   * license forbids storing the attachment), the progress bar view shows a
   * "Download via publisher" link instead of relying on a stored attachment. */
  landingPageUrl?: string;
};

export async function showBulkDownloadModal({
  trigger,
  work,
  gatedDownload,
  autoStartBulkDownload,
  purchased,
  readerMessageLimitReached = true,
  setReaderMessageLimitReached = () => {},
  citerightMarketingModalEligible = false,
  landingPageUrl,
}: Options): Promise<void> {
  const isPremium: boolean = Aedu.User?.current?.premium;
  const coronavirusWork = isCoronavirusWork(work);
  const shouldStartWorkDownloadOnUpsellClose = gatedDownload === true;

  if (isPremium || coronavirusWork || purchased) {
    const radarEnabledPromise = isPremium
      ? fetchFeatureEnabled()
      : Promise.resolve(false);

    Promise.all([
      import(
        './WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal'
      ),
      radarEnabledPromise,
    ]).then(([module, radarEnabled]) => {
      const PremiumModal = module.default;
      renderModal((onClose) => (
        <PremiumModal
          onClose={onClose}
          sourceWork={{
            id: work.id,
            title: work.title,
            slug: work.slug,
            doi: work.doi,
            hasDownloadableAttachment: !!work.attachment.id,
          }}
          downloadImmediately={autoStartBulkDownload}
          researchRadarEnabled={radarEnabled}
          landingPageUrl={landingPageUrl}
        />
      ));

      if (isPremium && trigger.startsWith('swp-')) {
        loadWonderchatScript();
      }
    });
  } else {
    // Read transactional pricing eligibility from the counts cache.
    // The cache is populated on page load so this await resolves immediately.
    const countsData = await fetchBulkDownloadCountsCached(Number(work.id));

    // Only bucket users who are eligible AND triggered from LISWP
    // regular upsell (not lookback/fallback)
    const eligibleForTransactionalPricing =
      countsData.eligible_for_single_purchase === true &&
      TRANSACTIONAL_PRICING_TRIGGERS.has(trigger);
    const transactionalPricingBucket = eligibleForTransactionalPricing
      ? TransactionalPricingV3AbTest()
      : undefined;
    const isTransactionalPricingVariant =
      transactionalPricingBucket === 'variant';

    const afterFirstModalClose = () => {
      if (isTransactionalPricingVariant) {
        // Show the transactional pricing modal.
        // fetchPreviewWorksCached is already settled (the first modal
        // fetched it), so both promises resolve almost immediately.
        const workIdNum = Number(work.id);
        fetchPreviewWorksCached(workIdNum)
          .catch(() => [])
          .then((bundleWorks) => {
            const sourceWork = bundleWorks.find(
              (w) => w.bucket === 'thePaperItself',
            );
            renderModal(
              (onClose) => (
                <TransactionalPricingModal
                  onClose={onClose}
                  workId={work.id}
                  workTitle={work.title}
                  workThumbnailUrl={sourceWork?.thumbnailUrl}
                  pdfCount={countsData.total_bulk_download_count}
                  priceInfo={countsData.single_purchase_price_info}
                />
              ),
              {
                afterClose: () => {
                  if (shouldStartWorkDownloadOnUpsellClose) {
                    maybeDownloadWork(
                      trigger,
                      work,
                      readerMessageLimitReached,
                      setReaderMessageLimitReached,
                      citerightMarketingModalEligible,
                    );
                  }
                  updateBulkDownloadLookback(trigger);
                },
              },
            );
          });
      } else {
        if (shouldStartWorkDownloadOnUpsellClose) {
          maybeDownloadWork(
            trigger,
            work,
            readerMessageLimitReached,
            setReaderMessageLimitReached,
            citerightMarketingModalEligible,
          );
        }
        updateBulkDownloadLookback(trigger);
      }
    };

    // ── R-variant dispatch ─────────────────────────────────────────────
    const ctx: ModalContext = { trigger };
    const arm = resolveArm(ctx);
    const downloadWork = () =>
      maybeDownloadWork(
        trigger,
        work,
        readerMessageLimitReached,
        setReaderMessageLimitReached,
        citerightMarketingModalEligible,
      );
    if (isRVariantArm(arm)) {
      try {
        const mod = await import(`./autotest/r-variants/${arm}`);
        if (mod.handleModal) {
          mod.handleModal({
            arm,
            renderModal,
            work,
            trigger,
            ctx,
            countsData,
            afterFirstModalClose,
            eligibleForTransactionalPricing,
            landingPageUrl,
            downloadWork,
          });
          return;
        }
      } catch {
        // Dynamic import failed — fall through to normal modal
      }
    }

    // When the user clicks the CTA, the modal snapshots the current filter
    // state and work IDs into sessionStorage (see
    // bulk_download_checkout_state.ts). After checkout, the premium modal
    // consumes that snapshot and uses the saved IDs for the auto-download so
    // the user gets exactly what they saw before upgrading. Storage is keyed
    // by the source work ID, has a 30-min TTL, and is cleared on dismiss.
    const landingPageHostname = landingPageUrl
      ? (() => {
          try {
            return new URL(landingPageUrl).hostname;
          } catch {
            return undefined;
          }
        })()
      : undefined;
    // For works where licenseAllowsAttachment is false, we cannot serve the
    // PDF ourselves — the paper must be fetched from the publisher's site.
    // In that case, the upsell secondary action should direct the user to
    // the paper externally (via landing page, DOI resolver, or Scholar)
    // rather than showing a plain "No thanks" that leaves them with nothing.
    //
    // This condition is intentionally strict: user-owned works and system-owned
    // works whose license permits hosting the attachment both get the default
    // "No thanks, I'll stay on the free tier" dismiss, even if they have a DOI.
    const attachmentExternalOnly = work.licenseAllowsAttachment === false;
    const fallbackUrl =
      !landingPageHostname && attachmentExternalOnly
        ? externalFallbackUrl(work.doi, work.title)
        : undefined;
    const externalUrl = landingPageHostname ? landingPageUrl : fallbackUrl;
    const secondaryActionLabel = landingPageHostname
      ? `No thanks, continue to ${landingPageHostname}`
      : work.doi && attachmentExternalOnly
        ? 'No thanks, view via DOI'
        : fallbackUrl
          ? 'No thanks, search Google Scholar'
          : undefined;

    // Phase 2 of the autotest-shaping rollout: the original modal tree
    // (`WorkCardsBulkDownloadUpsellModal/`) is now refactored to dispatch
    // on `AutotestBulkDownload.ts`. The sibling tree
    // (`WorkCardsBulkDownloadUpsellModalSibling/`) stays unrefactored as
    // the baseline. The new AB test
    // `bulk_download_2026_q2_modal_refactor` buckets users 50/50 between
    // them; CTR parity confirms the refactor preserves behavior.
    // Phase 1's `modal_duplication` test is now `active: false`.
    const modalRefactorBucket = ModalRefactorAbTest();

    const sharedProps = {
      onPrimaryCtaClicked: () =>
        navigateToCheckout(trigger, work.id, new Set()),
      impressionSource: bulkDownloadImpressionSource('upsell', trigger),
      sourceWork: work,
      transactionalPricingVariant: eligibleForTransactionalPricing
        ? isTransactionalPricingVariant
        : undefined,
      secondaryActionLabel,
      onSecondaryAction: externalUrl
        ? () => window.open(externalUrl, '_blank', 'noopener,noreferrer')
        : undefined,
      hideIncludedWorkValueProp: !!landingPageUrl,
    };

    if (modalRefactorBucket === 'control') {
      // Unrefactored sibling — old prop interface (no ctx)
      import('./WorkCardsBulkDownloadUpsellModalSibling').then(
        (upsellModule) => {
          const UpsellModal = upsellModule.default;
          renderModal(
            (onClose) => <UpsellModal onClose={onClose} {...sharedProps} />,
            { afterClose: afterFirstModalClose },
          );
        },
      );
    } else {
      // Refactored original — autotest-shaped, accepts ctx.
      import('./WorkCardsBulkDownloadUpsellModal').then((upsellModule) => {
        const UpsellModal = upsellModule.default;
        renderModal(
          (onClose) => (
            <UpsellModal onClose={onClose} ctx={ctx} {...sharedProps} />
          ),
          { afterClose: afterFirstModalClose },
        );
      });
    }
  }
}

function maybeDownloadWork(
  trigger: BulkDownloadTrigger,
  work: BulkDownloadableWork,
  readerMessageLimitReached: boolean,
  setReaderMessageLimitReached: (value: boolean) => void,
  citerightMarketingModalEligible: boolean,
): void {
  if (!giveGatedDownloadOnUpsellClose(trigger)) return;
  // Skip if there is no stored attachment (e.g. system-owned works with an
  // external landing page URL).  Clicking an empty href would navigate to the
  // current page rather than downloading a file.
  if (!work.attachment.url) return;
  const link = document.createElement('a');
  link.href = work.attachment.url;
  link.setAttribute('target', '_blank');
  link.setAttribute('style', 'display: none');
  document.body.appendChild(link);
  link.click();

  // CITE-232 AB test: in the variant arm, replace the post-download
  // "Send a note" modal with a CiteRight marketing bridge modal. Bucket only
  // when the user passes the encore-email eligibility filter so the cohort
  // matches the population that would otherwise have received an encore.
  // Encore email continues to send unchanged in both arms.
  if (citerightMarketingModalEligible) {
    const bucket = CiterightMarketingDownloadAbTest();
    if (bucket === 'variant') {
      import('js/liswp/modals_ds/CiterightMarketingModal').then((module) => {
        const { CiterightMarketingModal } = module;
        renderModal((onClose) => (
          <CiterightMarketingModal onClose={onClose} workId={work.id} />
        ));
      });
      return;
    }
  }

  // Skip reader message modal if no authors (system-owned works)
  const primaryAuthor = work.authors[0];
  if (readerMessageLimitReached || !primaryAuthor) return;

  import('js/liswp/modals_ds/ReaderMessageModal').then((module) => {
    const { ReaderMessageModal } = module;
    renderModal((onClose) => (
      <ToastProvider>
        <ReaderMessageContext.Provider
          value={{
            readerMessageLimitReached,
            setReaderMessageLimitReached,
            citerightMarketingModalEligible,
            setCiterightMarketingModalEligible: () => {},
          }}
        >
          <ReaderMessageModal
            onClose={onClose}
            primaryAuthor={{
              id: primaryAuthor.id,
              displayName: primaryAuthor.displayName,
              photo: primaryAuthor.photo,
            }}
            workId={work.id}
            attachmentId={work.attachment.id}
            readerMessageSource={bulkDownloadDownloadSource(trigger)}
          />
        </ReaderMessageContext.Provider>
      </ToastProvider>
    ));
  });
}
